import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, CheckCircle, Monitor, User, ClipboardList, Settings2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import api from '../lib/api';

const DEVICE_TYPES = [
  'Laptop',
  'Desktop / Tower',
  'Phone',
  'Tablet',
  'Printer',
  'Server',
  'Monitor / Display',
  'Network Equipment',
  'Other',
];

function Section({ icon: Icon, title, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div className="flex items-center gap-2" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, background: 'var(--accent)', borderRadius: 8, flexShrink: 0 }}>
          <Icon size={15} color="white" />
        </div>
        <h3 style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.02em' }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

export default function CheckInPage() {
  const { user, isStaff, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [config, setConfig]         = useState(null);
  const [categories, setCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [statuses, setStatuses]     = useState([]);
  const [techs, setTechs]           = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(null); // { ticket_number, client_email }

  // Account creation after submit (for public/tablet mode)
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [newPassword, setNewPassword]         = useState('');
  const [registering, setRegistering]         = useState(false);
  const [accountCreated, setAccountCreated]   = useState(false);

  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    device_type: '', device_make_model: '', device_serial: '', device_login: '', device_password: '',
    subject: '', description: '',
    // staff-only
    category_id: '', priority_id: '', assigned_to: '', status_id: '', internal_note: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get('/guest/config').then(d => setConfig(d.settings)).catch(() => {});
    api.get('/lookups/categories').then(d => setCategories(d.categories || [])).catch(() => {});
    api.get('/lookups/priorities').then(d => setPriorities(d.priorities || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!authLoading && isStaff) {
      api.get('/lookups/statuses').then(d => setStatuses(d.statuses || [])).catch(() => {});
      api.get('/users').then(d => setTechs((d.users || []).filter(u => ['admin', 'tech', 'super_admin'].includes(u.role)))).catch(() => {});
    }
  }, [authLoading, isStaff]);

  const logoUrl = config?.logo_url
    ? (config.logo_url.startsWith('/uploads/') ? `/api${config.logo_url}` : config.logo_url)
    : null;
  const siteName = config?.site_name || 'GT Computing';

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.email || !form.subject || !form.description) {
      return toast.error('Email, subject and description are required');
    }
    setSubmitting(true);
    try {
      const payload = {
        name:               form.name.trim()             || undefined,
        email:              form.email.trim(),
        phone:              form.phone.trim()             || undefined,
        device_type:        form.device_type              || undefined,
        device_make_model:  form.device_make_model.trim() || undefined,
        device_serial:      form.device_serial.trim()     || undefined,
        device_login:       form.device_login.trim()      || undefined,
        device_password:    form.device_password.trim()   || undefined,
        subject:            form.subject.trim(),
        description:        form.description.trim(),
      };
      if (isStaff) {
        if (form.category_id)       payload.category_id   = form.category_id;
        if (form.priority_id)       payload.priority_id   = form.priority_id;
        if (form.assigned_to)       payload.assigned_to   = form.assigned_to;
        if (form.status_id)         payload.status_id     = form.status_id;
        if (form.internal_note.trim()) payload.internal_note = form.internal_note.trim();
      }
      const result = await api.post('/checkin', payload);
      setSubmitted(result);
    } catch (err) {
      toast.error(err.message || 'Failed to submit check-in');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async () => {
    if (newPassword.length < 6) return toast.error('Password must be at least 6 characters');
    setRegistering(true);
    try {
      await api.post('/guest/register', { email: submitted.client_email, password: newPassword });
      setAccountCreated(true);
      toast.success('Account created! You can now log in.');
    } catch (err) {
      toast.error(err.message || 'Failed to create account');
    } finally {
      setRegistering(false);
    }
  };

  const resetForm = () => {
    setSubmitted(null);
    setCreatingAccount(false);
    setNewPassword('');
    setRegistering(false);
    setAccountCreated(false);
    setForm({
      name: '', email: '', phone: '',
      device_type: '', device_make_model: '', device_serial: '', device_login: '', device_password: '',
      subject: '', description: '',
      category_id: '', priority_id: '', assigned_to: '', status_id: '', internal_note: '',
    });
  };

  // ── Success screen ──────────────────────────────────────────
  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 24 }}>
        <div style={{ position: 'fixed', inset: 0, zIndex: 0, backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)', backgroundSize: '40px 40px', opacity: 0.5 }} />
        <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 520 }}>
          <div className="card card-lg" style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <CheckCircle size={52} color="var(--accent)" />
            </div>
            <h2 style={{ fontSize: 22, marginBottom: 8 }}>Check-In Complete!</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 4, fontSize: 15 }}>
              Ticket <strong>#{submitted.ticket_number}</strong> has been created.
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 24 }}>
              A confirmation has been sent to <strong>{submitted.client_email}</strong>.
            </p>

            {isStaff ? (
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                <button className="btn btn-secondary" onClick={() => navigate(`/tickets/${submitted.ticket_id}`)}>
                  View Ticket
                </button>
                <button className="btn btn-primary" onClick={resetForm}>
                  Check In Another
                </button>
              </div>
            ) : (
              <>
                {!accountCreated && !creatingAccount && (
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 20 }}>
                    <p style={{ fontSize: 13.5, marginBottom: 12, fontWeight: 500 }}>Want to track this ticket online?</p>
                    <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
                      Create a free account to check status and reply to updates.
                    </p>
                    <div className="flex gap-2" style={{ justifyContent: 'center' }}>
                      <button className="btn btn-secondary" onClick={resetForm}>Done</button>
                      <button className="btn btn-primary" onClick={() => setCreatingAccount(true)}>Create Account</button>
                    </div>
                  </div>
                )}
                {creatingAccount && !accountCreated && (
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 20 }}>
                    <p style={{ fontSize: 13.5, marginBottom: 16, fontWeight: 500 }}>
                      Set a password for <strong>{submitted.client_email}</strong>
                    </p>
                    <div className="form-group" style={{ textAlign: 'left' }}>
                      <label className="form-label">Password (min 6 characters)</label>
                      <input className="form-input" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" autoFocus />
                    </div>
                    <div className="flex gap-2" style={{ justifyContent: 'center' }}>
                      <button className="btn btn-secondary" onClick={() => setCreatingAccount(false)}>Cancel</button>
                      <button className="btn btn-primary" onClick={handleRegister} disabled={registering}>
                        {registering ? 'Creating…' : 'Create Account'}
                      </button>
                    </div>
                  </div>
                )}
                {accountCreated && (
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 20 }}>
                    <p style={{ color: 'var(--accent)', fontWeight: 500, marginBottom: 12 }}>Account created successfully!</p>
                    <div className="flex gap-2" style={{ justifyContent: 'center' }}>
                      <button className="btn btn-secondary" onClick={resetForm}>Check In Another</button>
                      <button className="btn btn-primary" onClick={() => navigate('/login')}>Sign In</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Form ────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '28px 16px 48px' }}>
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)', backgroundSize: '40px 40px', opacity: 0.5 }} />
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 720, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          {logoUrl ? (
            <img src={logoUrl} alt={siteName} style={{ maxHeight: 60, maxWidth: 220, objectFit: 'contain', marginBottom: 20 }} onError={e => e.target.style.display = 'none'} />
          ) : (
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, background: 'var(--accent)', borderRadius: 14, marginBottom: 20 }}>
              <Zap size={24} color="white" />
            </div>
          )}
          <h1 style={{ fontSize: 26, letterSpacing: '-0.04em', marginBottom: 6 }}>{siteName}</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Walk-In Check-In</p>

          {isStaff && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12, padding: '4px 12px', background: 'var(--accent)', borderRadius: 20, fontSize: 12, color: 'white', fontWeight: 500 }}>
              <Settings2 size={12} />
              Staff Mode — {user.full_name || user.email}
            </div>
          )}
        </div>

        <div className="card card-lg">
          <form onSubmit={handleSubmit}>

            {/* ── Client Information ── */}
            <Section icon={User} title="Client Information">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Full Name</label>
                  <input
                    className="form-input"
                    style={{ fontSize: 15 }}
                    placeholder="Jane Smith"
                    value={form.name}
                    onChange={e => set('name', e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email Address *</label>
                  <input
                    className="form-input"
                    style={{ fontSize: 15 }}
                    type="email"
                    placeholder="jane@example.com"
                    value={form.email}
                    onChange={e => set('email', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone Number</label>
                  <input
                    className="form-input"
                    style={{ fontSize: 15 }}
                    type="tel"
                    placeholder="(555) 555-5555"
                    value={form.phone}
                    onChange={e => set('phone', e.target.value)}
                  />
                </div>
              </div>
            </Section>

            <hr style={{ margin: '4px 0 24px', borderColor: 'var(--border)' }} />

            {/* ── Device / Item ── */}
            <Section icon={Monitor} title="Device / Item">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-group">
                  <label className="form-label">Device Type</label>
                  <select
                    className="form-select"
                    style={{ fontSize: 15 }}
                    value={form.device_type}
                    onChange={e => set('device_type', e.target.value)}
                  >
                    <option value="">Select type…</option>
                    {DEVICE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Make & Model</label>
                  <input
                    className="form-input"
                    style={{ fontSize: 15 }}
                    placeholder="e.g. Dell XPS 15"
                    value={form.device_make_model}
                    onChange={e => set('device_make_model', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Serial Number</label>
                  <input
                    className="form-input"
                    style={{ fontSize: 15 }}
                    placeholder="Optional"
                    value={form.device_serial}
                    onChange={e => set('device_serial', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Login / Username</label>
                  <input
                    className="form-input"
                    style={{ fontSize: 15 }}
                    placeholder="Windows login, Apple ID, etc."
                    value={form.device_login}
                    onChange={e => set('device_login', e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input
                    className="form-input"
                    style={{ fontSize: 15 }}
                    placeholder="Device password / PIN"
                    value={form.device_password}
                    onChange={e => set('device_password', e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
              </div>
            </Section>

            <hr style={{ margin: '4px 0 24px', borderColor: 'var(--border)' }} />

            {/* ── Problem Description ── */}
            <Section icon={ClipboardList} title="Problem Description">
              <div className="form-group">
                <label className="form-label">Subject *</label>
                <input
                  className="form-input"
                  style={{ fontSize: 15 }}
                  placeholder="Briefly describe the issue"
                  value={form.subject}
                  onChange={e => set('subject', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">What's the issue? *</label>
                <textarea
                  className="form-textarea"
                  style={{ minHeight: 150, fontSize: 15, lineHeight: 1.6 }}
                  placeholder="Describe the problem in as much detail as possible. What happened? When did it start? What have you tried?"
                  value={form.description}
                  onChange={e => set('description', e.target.value)}
                  required
                />
              </div>
            </Section>

            {/* ── Staff Details (visible only when staff is logged in) ── */}
            {isStaff && (
              <>
                <hr style={{ margin: '4px 0 24px', borderColor: 'var(--border)' }} />
                <Section icon={Settings2} title="Staff Details">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <div className="form-group">
                      <label className="form-label">Category</label>
                      <select className="form-select" value={form.category_id} onChange={e => set('category_id', e.target.value)}>
                        <option value="">Auto-detect…</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Priority</label>
                      <select className="form-select" value={form.priority_id} onChange={e => set('priority_id', e.target.value)}>
                        <option value="">Default (Medium)</option>
                        {priorities.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Status</label>
                      <select className="form-select" value={form.status_id} onChange={e => set('status_id', e.target.value)}>
                        <option value="">Default</option>
                        {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Assign To</label>
                      <select className="form-select" value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)}>
                        <option value="">Unassigned</option>
                        {techs.map(t => <option key={t.id} value={t.id}>{t.full_name || t.email}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Internal Note</label>
                    <textarea
                      className="form-textarea"
                      style={{ minHeight: 90 }}
                      placeholder="Private note visible only to staff (optional)"
                      value={form.internal_note}
                      onChange={e => set('internal_note', e.target.value)}
                    />
                  </div>
                </Section>
              </>
            )}

            <div className="flex gap-3" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
              {isStaff && (
                <button type="button" className="btn btn-secondary" onClick={() => navigate('/tickets')}>
                  Cancel
                </button>
              )}
              <button type="submit" className="btn btn-primary" disabled={submitting} style={{ minWidth: 140, fontSize: 15 }}>
                {submitting
                  ? <><div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Submitting…</>
                  : 'Submit Check-In'}
              </button>
            </div>
          </form>
        </div>

        {!isStaff && (
          <p style={{ textAlign: 'center', marginTop: 16, color: 'var(--text-muted)', fontSize: 12 }}>
            Already have an account?{' '}
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 12, padding: '2px 6px' }} onClick={() => navigate('/login')}>
              Sign in
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
